import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { t } from 'boot/i18n';
import {
  useSchoolData,
  type JoinClassError,
} from 'src/modules/School/composables/useSchoolData';
import { joinErrorPresentation } from 'src/modules/School/utilities/joinErrorPresentation';
import { notifyImportant } from 'src/shared/utilities/notify';

/**
 * Invite-link enrollment. A teacher shares a link carrying a classDocumentId; using it
 * registers the student as `pending` (the link is shareable, so it still requires the
 * teacher's approval). Not-yet-authenticated students are bounced to login by the auth
 * guard, so the pending class is stashed here and consumed right after a successful
 * student sign-in.
 *
 * Every invite-link join funnels through the `school.class.join` route so the join
 * page is the single place that renders progress, success, and a human-readable error
 * page (declined / removed / term ended / …) — sign-in and registration flows redirect
 * here instead of joining inline. That redirect is `boot/auth`'s job: it owns
 * post-sign-in routing, so the invite is honoured no matter which login door the
 * student used.
 */
export const PENDING_JOIN_KEY = 'flushSchoolPendingJoin';

interface PendingJoin {
  classDocumentId: string;
  semesterDocumentId?: string;
}

/** Stash an invite target (class + semester) for consumption after sign-in. */
export function stashPendingJoin(pending: PendingJoin): void {
  localStorage.setItem(PENDING_JOIN_KEY, JSON.stringify(pending));
}

/**
 * Read and clear the stashed invite target. Exported for `boot/auth`, which owns
 * post-sign-in routing: consuming the stash there means the invite survives no
 * matter which door the student signed in through.
 */
export function readPendingJoin(): PendingJoin | null {
  const raw = localStorage.getItem(PENDING_JOIN_KEY);
  localStorage.removeItem(PENDING_JOIN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingJoin;
    if (parsed?.classDocumentId) return parsed;
  } catch {
    // Legacy value: a bare classDocumentId string (pre-semester invite links).
    return { classDocumentId: raw };
  }
  return null;
}

export type JoinStatus = 'joining' | 'error';

export function useStudentJoin() {
  const router = useRouter();
  const { joinClass } = useSchoolData();

  // Reactive state consumed by the join view to render spinner vs. error page.
  const joinStatus = ref<JoinStatus>('joining');
  const joinError = ref<JoinClassError | null>(null);

  /**
   * Send the student to the dashboard carrying the reason the join failed.
   *
   * A terminal failure (removed from the class, declined, term ended, …) has
   * nothing to retry, so parking the student on a dead-end page helps no one —
   * the dashboard at least shows the class card whose status chip says the same
   * thing. The explanation rides along as a centered, stay-until-closed toast so
   * it cannot be missed or auto-dismissed before it is read.
   */
  async function reportJoinFailure(error: JoinClassError | null): Promise<void> {
    const presentation = joinErrorPresentation(error);
    notifyImportant({
      message: t(presentation.messageKey),
      caption: t(presentation.titleKey),
      icon: presentation.icon,
    });
    await router.push({ name: 'dashboard' });
  }

  /**
   * Perform the invite-link join. On success, land on the dashboard.
   * On a retryable failure, expose the parsed error so the view renders the error
   * page with its "Try again" action; anything terminal goes to the dashboard with
   * the explanation in tow.
   */
  async function runJoin(
    classDocumentId: string,
    semesterDocumentId?: string,
  ): Promise<void> {
    if (!classDocumentId) {
      joinError.value = null;
      await reportJoinFailure(null);
      return;
    }

    joinStatus.value = 'joining';
    joinError.value = null;

    const { enrollment, error } = await joinClass(
      classDocumentId,
      semesterDocumentId,
    );

    if (error) {
      joinError.value = error;
      // Only a transient failure (network / 5xx) is worth a retry button; every
      // other case is explained on the dashboard instead. `joinStatus` stays
      // 'joining' while navigating away so the error page never flashes.
      if (joinErrorPresentation(error).showRetry) {
        joinStatus.value = 'error';
        return;
      }
      await reportJoinFailure(error);
      return;
    }

    if (enrollment) {
      // No toast: the student lands on the dashboard, where the class card's
      // status chip already says the enrollment is pending approval.
      await router.push({ name: 'dashboard' });
      return;
    }

    // No enrollment and no error (e.g. empty class id) — fall back to the dashboard.
    await router.push({ name: 'dashboard' });
  }

  return {
    joinStatus,
    joinError,
    runJoin,
  };
}
